import { createReducer } from '@reduxjs/toolkit';
import { switchAccountSuccess } from 'store/account/actions';
import { Action } from 'modules/types/common';
import { Platform } from 'react-native';
import { defaultInitialState, DefaultState } from 'store/setLocalStore';
import { StepTypes, SkipPaymentFlags } from 'lib/constants/preRegister';
import * as actions from './actions';
import {
  PreRegistrationState,
  PreRegistration,
  UpdateSkipPaymentFlagPayload,
  PatientInfo,
  VisitDetail,
  GuarantorInfo,
  SkipPaymentFlagTypes,
  CheckListItem
} from './types';

const getLocalStorage = () => {
  if (Platform.OS === 'web') {
    const data = window.sessionStorage.getItem('PRE_REG');
    if (data) {
      return JSON.parse(data);
    }
  }
  return null;
};
const storedPreRegData = getLocalStorage();

export const PreRegistrationInitialState: PreRegistrationState = {
  isFetching: false,
  error: null,
  jwtToken: storedPreRegData ? storedPreRegData.jwtToken : null,
  eventId: storedPreRegData ? storedPreRegData.eventId : null,
  data: {
    patientInfo: {} as PatientInfo,
    guarantorInfo: {} as GuarantorInfo,
    complete: false,
    linkExpired: false,
    cancelled: false,
    demographicTypes: {
      ethnicity: [],
      maritalStatus: [],
      primaryLanguage: [],
      religion: [],
      race: []
    },
    visitDetail: {} as VisitDetail,
    checkList: []
  },
  pinSend: defaultInitialState(),
  pinValidate: defaultInitialState(),
  skipPaymentFlags: {
    mspqException: false,
    guarantorInfoChanged: false,
    insuranceInfoChanged: false,
    selfPay: false,
    selfPayElect: false
  },
  personInfoChanged: false,
  updateCollectionAttempt: false
};

const setPaymentFlags = (checklist: CheckListItem[]): Partial<SkipPaymentFlagTypes> => {
  const result: Partial<SkipPaymentFlagTypes> = {};
  checklist.forEach(item => {
    if (item.type === StepTypes.INSURANCE) {
      if (item.status === 'CHANGE') {
        result[SkipPaymentFlags.INSURANCE_INFO_CHANGED] = true;
      }
    }
    if (item.type === StepTypes.GUARANTOR_INFO) {
      if (item.status === 'CHANGE') {
        result[SkipPaymentFlags.GUARANTOR_INFO_CHANGED] = true;
      }
    }
  });

  return result;
};

// This is a holder for what graphql returns
const PreRegistrationReducer = createReducer(PreRegistrationInitialState, {
  [actions.PRE_REGISTRATION]: state => {
    state.isFetching = true;
    state.error = null;
  },
  [actions.PRE_REGISTRATION_SUCCESS]: (state: PreRegistrationState, action: Action) => {
    state.isFetching = false;
    state.data = action.payload.data.preRegistration;
    if (state?.data?.checkList) {
      state.skipPaymentFlags = {
        ...state.skipPaymentFlags,
        ...setPaymentFlags(state.data.checkList)
      };
    }
  },
  [actions.PRE_REGISTRATION_FAIL]: (state: PreRegistrationState, action: Action) => {
    state.isFetching = false;
    state.data = undefined;
    state.error = action.error;
  },
  [actions.PRE_REGISTRATION_RESET]: () => PreRegistrationInitialState,
  [switchAccountSuccess.type]: () => PreRegistrationInitialState,
  [actions.UPDATE_PRE_REGISTRATION_CHECKLIST]: (state, action) => {
    const { checkList } = action.payload;
    if (!state.data) {
      // Should never happen, but just in case.
      state.data = {} as PreRegistration;
    }
    state.data.checkList = checkList;
  },
  [actions.UPDATE_PRE_REGISTRATION_CHECKLIST_STEP]: (state, action) => {
    if (!state.data) {
      // Should never happen, but just in case.
      state.data = {} as PreRegistration;
      return;
    }
    const index = state.data.checkList.findIndex(item => item.id === action.payload.id);
    if (index >= 0) {
      state.data.checkList[index] = action.payload;
    }
  },
  [actions.UPDATE_PROCESS_STATUS]: (state, action) => {
    if (!state.data) {
      state.data = {} as PreRegistration;
      return;
    }
    state.data.complete = action.payload;
  },
  [actions.PRE_REGISTRATION_JWT_TOKEN]: (state, action) => {
    const { jwtToken, eventId } = action.payload;
    window.sessionStorage.setItem(
      'PRE_REG',
      JSON.stringify({
        jwtToken,
        eventId
      })
    );
    return {
      ...state,
      jwtToken,
      eventId
    };
  },
  [actions.UPDATE_SKIP_PAYMENT_FLAG]: (
    state: PreRegistrationState,
    action: { payload: UpdateSkipPaymentFlagPayload }
  ) => {
    const { key, value } = action.payload;
    state.skipPaymentFlags[key as keyof SkipPaymentFlagTypes] = value;
  },
  [actions.SEND_PIN]: state => {
    state.pinSend.isFetching = true;
    state.pinSend.error = null;
  },
  [actions.SEND_PIN_SUCCESS]: (state: { pinSend: DefaultState }, action: Action) => {
    state.pinSend.isFetching = false;
    state.pinSend.data = action.payload.data;
  },
  [actions.SEND_PIN_FAIL]: (state: { pinSend: DefaultState }, action: Action) => {
    state.pinSend.isFetching = false;
    state.pinSend.data = undefined;
    state.pinSend.error = action.error;
  },
  [actions.VALIDATE_PIN]: state => {
    state.pinValidate.isFetching = true;
    state.pinValidate.error = null;
  },
  [actions.VALIDATE_PIN_FAIL]: (state: { pinValidate: DefaultState }, action: Action) => {
    state.pinValidate.isFetching = false;
    state.pinValidate.data = undefined;
    state.pinValidate.error = action.error;
  },
  [actions.VALIDATE_PIN_SUCCESS]: (state: { pinValidate: DefaultState }, action: Action) => {
    state.pinValidate.isFetching = false;
    state.pinValidate.data = action.payload.data;
  },
  [actions.UPDATE_SELF_PAYMENT_FLAG]: (state, action: Action) => {
    state.skipPaymentFlags.selfPay = action.payload.data;
  },
  [actions.UPDATE_PERSONAL_INFORMATION]: (state, action: Action) => {
    state.personInfoChanged = action.payload;
  },
  [actions.UPDATE_COLLECTION_ATTEMPT_STATUS]: (state, action: Action) => {
    state.updateCollectionAttempt = true;
  }
});

export default PreRegistrationReducer;
